import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2, Rocket } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Connections from "@/pages/Connections";
import NewProject from "@/pages/NewProject";
import ProjectDetail from "@/pages/ProjectDetail";

function FullLoader() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-5 px-6" data-testid="app-loading">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-md bg-white flex items-center justify-center">
          <Rocket className="h-5 w-5 text-black" strokeWidth={2} />
        </div>
        <span className="font-heading text-xl font-bold tracking-tight">DeployBridge</span>
      </div>
      <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
      {slow && (
        <p className="text-sm text-neutral-500 text-center max-w-sm leading-relaxed" data-testid="app-loading-slow">
          Waking up the server… the first load after a while can take up to a minute on the free tier. Hang tight.
        </p>
      )}
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/connections" element={<Protected><Connections /></Protected>} />
          <Route path="/new" element={<Protected><NewProject /></Protected>} />
          <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" />
    </AuthProvider>
  );
}

export default App;
